import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col, Table } from 'react-bootstrap';
import { Typography, Box, TableSortLabel } from '@material-ui/core';
import _ from 'lodash';

import { StatisticCard } from './ReportsViews';
import Loader3Orange from '../../general/Loader3Orange';
import { FaUserTie, FaClipboardList, FaFileSignature, FaCoins } from 'react-icons/fa';

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD', // Or your preferred currency
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const BrokersTab = ({ brokerInfo, startDate, endDate, translationsDashboard }) => {
    const [loading, setLoading] = useState(true);
    const [brokerStats, setBrokerStats] = useState([]);
    const [error, setError] = useState(null);

    // State for table sorting
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('totalPremium');

    useEffect(() => {
        const brokerageId = brokerInfo?.information?.brokerages?.[0]?.code;
        if (!brokerageId) {
            setError("Brokerage information is not available.");
            setLoading(false);
            return;
        }

        const fetchBrokerStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("jwt");
                const payload = {
                    startDate: startDate,
                    endDate: endDate,
                };
                
                const response = await axios.post(
                    `${process.env.REACT_APP_BASICSERVICE}/api/brokerages/${brokerageId}/brokers/stats`,
                    payload,
                    { headers: { "x-auth-token": token } }
                );
                
                setBrokerStats(response.data);

            } catch (err) {
                console.error("Error fetching broker stats:", err.response || err);
                setError("Failed to load broker statistics.");
            } finally {
                setLoading(false);
            }
        };

        fetchBrokerStats();

    }, [brokerInfo, startDate, endDate]);

    // Calculate overall KPIs from the detailed broker stats
    const kpis = React.useMemo(() => {
        if (!brokerStats || brokerStats.length === 0) {
            return { totalBrokers: 0, totalProspects: 0, totalPolicies: 0, totalPremium: 0 };
        }
        return {
            totalBrokers: brokerStats.length,
            totalProspects: _.sumBy(brokerStats, 'prospectsCreated'),
            totalPolicies: _.sumBy(brokerStats, 'policiesBound'),
            totalPremium: _.sumBy(brokerStats, 'totalPremium'),
        };
    }, [brokerStats]);
    
    // Handle sorting request
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Sort the data for the table
    const sortedBrokerStats = React.useMemo(() => {
        return _.orderBy(brokerStats, [orderBy], [order]);
    }, [brokerStats, order, orderBy]);

    if (loading) return <Loader3Orange />;

    if (error) {
        return (
            <Box p={3} style={{ textAlign: 'center' }}>
                <img width={"300px"} src="https://error404.fun/img/illustrations/20.png" alt="Error"/>
                <Typography color="error" variant="h6">{error}</Typography>
            </Box>
        );
    }
    
    if (!brokerStats || brokerStats.length === 0) {
        return (
            <Col md={12} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: '5rem' }}>
                <div><img width={"400px"} src="https://error404.fun/img/illustrations/08.png" alt="No Data"/></div>
                <div><h3 style={{ fontFamily: "GibsonSemiBd", color: "rgb(63, 68, 90)" }}>{translationsDashboard.noDataFound}</h3></div>
            </Col>
        );
    }
    
    const headCells = [
        { id: 'brokerName', label: 'Broker Name' },
        { id: 'prospectsCreated', label: 'Prospects Created' },
        { id: 'policiesBound', label: 'Policies Bound' },
        { id: 'totalPremium', label: 'Total Premium' },
        { id: 'avgPremium', label: 'Avg. Premium / Policy' },
    ];

    return (
        <Box p={3}>
            {/* KPI Cards */}
            <Row className="mb-4">
                <Col>
                    <div className="chartTitle" style={{justifyContent: 'center', marginBottom: '1rem'}}>
                        Team Summary
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                        <StatisticCard cardInfo={{ icon: FaUserTie, title: 'Active Brokers', data: kpis.totalBrokers }} />
                        <StatisticCard cardInfo={{ icon: FaClipboardList, title: 'Total Prospects', data: kpis.totalProspects }} />
                        <StatisticCard cardInfo={{ icon: FaFileSignature, title: 'Total Policies', data: kpis.totalPolicies }} />
                        <StatisticCard cardInfo={{ icon: FaCoins, title: 'Total Premium', data: formatCurrency(kpis.totalPremium) }} />
                    </div>
                </Col>
            </Row>

            {/* Broker Performance Table */}
            <Row>
                <Col>
                    <div className="chartTitle" style={{justifyContent: 'center', marginBottom: '1rem'}}>
                        Individual Performance
                    </div>
                    <Table striped bordered hover responsive="sm" className="broker-table">
                        <thead>
                            <tr>
                                {headCells.map((headCell) => (
                                    <th key={headCell.id} onClick={() => handleRequestSort(headCell.id)}>
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedBrokerStats.map((broker) => (
                                <tr key={broker.brokerId}>
                                    <td>{broker.brokerName}</td>
                                    <td>{broker.prospectsCreated}</td>
                                    <td>{broker.policiesBound}</td>
                                    <td>{formatCurrency(broker.totalPremium)}</td>
                                    <td>{formatCurrency(broker.avgPremium)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Box>
    );
};

export default BrokersTab;